/* eslint-disable complexity */
import moment from 'moment';
import axios from 'axios';

export const LINKEDIN_TYPE = 1;

export const RESOURCE_FIELDS_LIST = {
  other_education__degree: 'courses',
  other_education__field_of_study: 'branches',
  other_education__college: 'colleges',
  other_education__specialization: 'specializations',
  employment__designation: 'designations',
  employment__department: 'departments',
  employment__employer: 'companies',
  employment__domain: 'jobdomains',
};

export const ENTITY_DEFAULT_FIELDS = {
  education: ['branch', 'college', 'course', 'specialization', 'year_of_graduation', 'year_of_joining', 'class_year'],
  other_education: [
    'college',
    'degree',
    'field_of_study',
    'specialization',
    'name',
    'year_of_graduation',
    'year_of_joining',
  ],
  employment: ['designation', 'domain', 'employer', 'start_year', 'end_year', 'current', 'start_month', 'end_month'],
};

export const YEAR_FIELDS = [
  'education__year_of_joining',
  'other_education__year_of_joining',
  'education__year_of_graduation',
  'other_education__year_of_graduation',
  'employment__start_year',
  'employment__end_year',
];

export const MONTH_FIELDS = ['employment__start_month', 'employment__end_month'];

export const YEAR_VALIDATION_CONFIG = {
  employment__end_year: 'employment__start_year',
  education__year_of_graduation: 'education__year_of_joining',
  other_education__year_of_graduation: 'other_education__year_of_joining',
};

export const FIELD_DEPENDENCIES = {
  education__branch: {
    triggerFields: ['education__specialization'],
    message: 'Branch is required',
  },
  education__course: {
    triggerFields: ['education__specialization'],
    message: 'Course is required',
  },
  other_education__college: {
    triggerFields: [
      'other_education__degree',
      'other_education__field_of_study',
      'other_education__specialization',
      'other_education__year_of_joining',
      'other_education__year_of_graduation',
    ],
    message: 'College is required',
  },
  employment__employer: {
    triggerFields: [
      'employment__designation',
      'employment__domain',
      'employment__start_month',
      'employment__start_year',
      'employment__end_month',
      'employment__end_year',
      'employment__current',
    ],
    message: 'Employer is required',
  },
  phone_number__number: {
    triggerFields: ['phone_number__type'],
    message: 'Phone number is required',
  },
  phone_number__type: {
    triggerFields: ['phone_number__number'],
    message: 'Phone number type is required',
  },
  address__line1: {
    triggerFields: [
      'address__line2',
      'address__location',
      'address__state',
      'address__country',
      'address__county',
      'address__zip_code',
      'address__type',
    ],
    message: 'Address line 1 is required',
  },
  address__type: {
    triggerFields: [
      'address__line2',
      'address__location',
      'address__state',
      'address__country',
      'address__county',
      'address__zip_code',
      'address__line1',
    ],
    message: 'Address type is required',
  },
};

export const FIELD_DISABLE_CONFIG = {
  employment__current: {
    disableFields: ['employment__end_year', 'employment__end_month'],
    condition: (value) => {
      if (Array.isArray(value)) {
        return value.length > 0 && (value.includes('Yes') || value.includes('true') || value.includes('1'));
      }

      if (typeof value === 'string') {
        return value === 'Yes' || value === 'true' || value === '1';
      }

      if (typeof value === 'boolean') {
        return value === true;
      }

      if (typeof value === 'number') {
        return value === 1;
      }

      return false;
    },
  },
};

export function getStepTwoFieldKey(field) {
  return field.entity_path ? `${field.entity_path}__${field.path}` : field.path;
}

function getGooglePlaceValue(location) {
  if (!location) {
    return undefined;
  }

  return {
    gpid: location.gpid,
    name: location.name,
  };
}

function isValueFilled(value) {
  if (!value) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim() !== '';
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value).length > 0;
  }

  return true;
}

function hasTriggerFieldsWithValues(fieldPath, allFormValues) {
  const dependency = FIELD_DEPENDENCIES[fieldPath];
  if (!dependency) {
    return false;
  }

  return dependency.triggerFields.some((triggerField) => isValueFilled(allFormValues[triggerField]));
}

function createDependentFieldValidator(field, form) {
  return async (_rule, value) => {
    if (field.required) {
      if (!isValueFilled(value)) {
        return Promise.reject(new Error(`${field.label} is required`));
      }
      return Promise.resolve();
    }

    const fieldPath = getStepTwoFieldKey(field);
    const dependency = FIELD_DEPENDENCIES[fieldPath];
    if (!dependency) {
      return Promise.resolve();
    }

    const allFormValues = form.getFieldsValue();
    if (hasTriggerFieldsWithValues(fieldPath, allFormValues) && !isValueFilled(value)) {
      return Promise.reject(new Error(dependency.message));
    }

    return Promise.resolve();
  };
}

function createYearValidator(currentFieldLabel, compareFieldPath, compareFieldLabel, isGreaterThan, form) {
  return async (_rule, value) => {
    if (!value) {
      return Promise.resolve();
    }

    const compareValue = form.getFieldValue(compareFieldPath);
    if (!compareValue) {
      return Promise.resolve();
    }

    const currentYear = value.format('YYYY');
    const compareYear = compareValue.format('YYYY');

    if (isGreaterThan && currentYear < compareYear) {
      return Promise.reject(new Error(`${currentFieldLabel} cannot be earlier than ${compareFieldLabel}`));
    }

    if (!isGreaterThan && currentYear > compareYear) {
      return Promise.reject(new Error(`${currentFieldLabel} cannot be later than ${compareFieldLabel}`));
    }

    return Promise.resolve();
  };
}

export function getStepTwoValidationRules(field, form, fields) {
  const fieldPath = getStepTwoFieldKey(field);
  const rules = [];

  if (field.type === 'url') {
    rules.push({ type: 'url', message: `Please enter a valid ${field.label.toLowerCase()}` });
  }

  if (field.required) {
    rules.unshift({ required: true, message: `${field.label} is required` });
  }

  if (FIELD_DEPENDENCIES[fieldPath]) {
    rules.push({ validator: createDependentFieldValidator(field, form) });
  }

  const greaterField = Object.keys(YEAR_VALIDATION_CONFIG).find((key) => fieldPath.endsWith(key));
  const smallerField = Object.entries(YEAR_VALIDATION_CONFIG).find(([, value]) => fieldPath.endsWith(value));

  if (greaterField) {
    const compareFieldPath = fieldPath.replace(greaterField, YEAR_VALIDATION_CONFIG[greaterField]);
    const compareField = fields.find((formField) => getStepTwoFieldKey(formField) === compareFieldPath);
    rules.push({
      validator: createYearValidator(
        field.label,
        compareFieldPath,
        compareField?.label || compareFieldPath,
        true,
        form,
      ),
    });
  }

  if (smallerField) {
    const compareFieldPath = fieldPath.replace(smallerField[1], smallerField[0]);
    const compareField = fields.find((formField) => getStepTwoFieldKey(formField) === compareFieldPath);
    rules.push({
      validator: createYearValidator(
        field.label,
        compareFieldPath,
        compareField?.label || compareFieldPath,
        false,
        form,
      ),
    });
  }

  return rules;
}

export function getDisabledStepTwoFields(allFormValues) {
  const disabledFields = [];

  Object.entries(FIELD_DISABLE_CONFIG).forEach(([triggerField, config]) => {
    if (config.condition(allFormValues[triggerField])) {
      disabledFields.push(...config.disableFields);
    }
  });

  return Array.from(new Set(disabledFields));
}

function normalizeCustomFieldValue(customField) {
  if (!customField || !customField.values || !customField.values.length) {
    return undefined;
  }

  const fieldValue = customField.values[0].value;
  if (Array.isArray(fieldValue)) {
    return fieldValue.map((item) => item.content);
  }

  if (fieldValue && typeof fieldValue === 'object' && Object.prototype.hasOwnProperty.call(fieldValue, 'content')) {
    return fieldValue.content;
  }

  return fieldValue;
}

function getFileNameFromUrl(url, fallbackLabel) {
  if (!url || typeof url !== 'string') {
    return fallbackLabel;
  }

  const lastSegment = url.split('/').filter(Boolean).slice(-1)[0];
  return lastSegment ? decodeURIComponent(lastSegment.split('?')[0]) : fallbackLabel;
}

function isImageUrl(url) {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const sanitizedUrl = url.split('?')[0].toLowerCase();
  return ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'].some((extension) => sanitizedUrl.endsWith(extension));
}

function getEntityRecord(profileData, entityPath) {
  if (entityPath === 'education') {
    return profileData.educations?.[0];
  }

  if (entityPath === 'other_education') {
    return profileData.other_educations?.[0];
  }

  if (entityPath === 'employment') {
    return profileData.employments?.[0];
  }

  if (entityPath === 'address') {
    return profileData.addresses?.[0];
  }

  if (entityPath === 'phone_number') {
    return profileData.phone_numbers?.[0];
  }

  return profileData;
}

function getLocationValue(location) {
  if (!location) {
    return undefined;
  }

  return location.name || location.formatted_address;
}

function isDateLikeField(field) {
  const fieldKey = getStepTwoFieldKey(field);
  return field.type === 'date' || YEAR_FIELDS.includes(fieldKey) || MONTH_FIELDS.includes(fieldKey);
}

function getDateValue(fieldKey, value) {
  if (!value) {
    return undefined;
  }

  if (moment.isMoment(value)) {
    return value;
  }

  if (YEAR_FIELDS.includes(fieldKey)) {
    return moment(String(value), 'YYYY');
  }

  if (MONTH_FIELDS.includes(fieldKey)) {
    return moment(String(value), 'MM');
  }

  return moment(value, 'YYYY-MM-DD');
}

function normalizeInitialFieldValue(field, value) {
  if (value === undefined || value === null) {
    return value;
  }

  if (isDateLikeField(field)) {
    return getDateValue(getStepTwoFieldKey(field), value);
  }

  return value;
}

function getScalarValue(record, field) {
  if (!record) {
    return undefined;
  }

  if (field.path === 'present_picture' || field.type === 'file') {
    return undefined;
  }

  if (isDateLikeField(field)) {
    return getDateValue(getStepTwoFieldKey(field), record[field.path]);
  }

  if (field.type === 'location') {
    return getLocationValue(record[field.path]);
  }

  // Checkbox.Group needs an array; boolean entity fields (e.g. employment.current) crash it otherwise.
  if (field.type === 'checkbox' && typeof record[field.path] === 'boolean') {
    return record[field.path] ? ['Yes'] : [];
  }

  if (['select', 'multi_select', 'checkbox', 'radio'].includes(field.type) && Array.isArray(record[field.path])) {
    return record[field.path].map((item) => item.id);
  }

  if (
    record[field.path] &&
    typeof record[field.path] === 'object' &&
    Object.prototype.hasOwnProperty.call(record[field.path], 'name')
  ) {
    return record[field.path].name;
  }

  return record[field.path];
}

function getLinkedInValue(profileData) {
  const linkedInLink = (profileData.social_links || []).find(
    (link) => link.type === LINKEDIN_TYPE || link.type_display === 'LinkedIn',
  );

  return linkedInLink?.link;
}

function getInitialFileUrl(profileData, field) {
  const record = getEntityRecord(profileData, field.entity_path);
  const customFields = record?.custom_fields || {};

  if (field.path === 'present_picture') {
    return profileData.present_picture;
  }

  if (record && Object.prototype.hasOwnProperty.call(customFields, field.path)) {
    return normalizeCustomFieldValue(customFields[field.path]);
  }

  if (
    !field.entity_path &&
    profileData.custom_fields &&
    Object.prototype.hasOwnProperty.call(profileData.custom_fields, field.path)
  ) {
    return normalizeCustomFieldValue(profileData.custom_fields[field.path]);
  }

  return record?.[field.path];
}

export function getStepTwoInitialValues(profileData, fields) {
  if (!profileData || isEmptyObject(profileData) || !fields.length) {
    return {};
  }

  const initialValues = {};

  fields.forEach((field) => {
    if (field.type === 'section') {
      return;
    }

    if (field.path === 'linkedin_url' && !field.entity_path) {
      initialValues.linkedin_url = getLinkedInValue(profileData);
      return;
    }

    if (field.type === 'file') {
      const initialFileUrl = getInitialFileUrl(profileData, field);
      initialValues[getStepTwoFieldKey(field)] = typeof initialFileUrl === 'string' ? initialFileUrl : undefined;
      return;
    }

    const fieldKey = getStepTwoFieldKey(field);
    const record = getEntityRecord(profileData, field.entity_path);
    const customFields = record?.custom_fields || {};

    if (record && Object.prototype.hasOwnProperty.call(customFields, field.path)) {
      initialValues[fieldKey] = normalizeInitialFieldValue(field, normalizeCustomFieldValue(customFields[field.path]));
      return;
    }

    if (
      !field.entity_path &&
      profileData.custom_fields &&
      Object.prototype.hasOwnProperty.call(profileData.custom_fields, field.path)
    ) {
      initialValues[fieldKey] = normalizeInitialFieldValue(
        field,
        normalizeCustomFieldValue(profileData.custom_fields[field.path]),
      );
      return;
    }

    initialValues[fieldKey] = getScalarValue(record, field);
  });

  return initialValues;
}

export function getStepTwoInitialFiles(profileData, fields) {
  if (!profileData || isEmptyObject(profileData) || !fields.length) {
    return {};
  }

  return fields.reduce((result, field) => {
    if (field.type !== 'file') {
      return result;
    }

    const fieldKey = getStepTwoFieldKey(field);
    const fileUrl = getInitialFileUrl(profileData, field);

    if (!fileUrl || typeof fileUrl !== 'string') {
      return result;
    }

    result[fieldKey] = {
      uid: fieldKey,
      name: getFileNameFromUrl(fileUrl, field.label),
      url: fileUrl,
      status: 'done',
      ...(field.path === 'present_picture' || isImageUrl(fileUrl) ? { thumbUrl: fileUrl } : {}),
    };
    return result;
  }, {});
}

export function getStepTwoInitialLocationValues(profileData, fields) {
  if (!profileData || isEmptyObject(profileData) || !fields.length) {
    return {};
  }

  return fields.reduce((result, field) => {
    if (field.type === 'section' || field.type !== 'location') {
      return result;
    }

    const record = getEntityRecord(profileData, field.entity_path);
    const locationValue = getGooglePlaceValue(record?.[field.path]);

    if (locationValue) {
      result[getStepTwoFieldKey(field)] = locationValue;
    }

    return result;
  }, {});
}

function isEmptyObject(value) {
  return !value || Object.keys(value).length === 0;
}

function formatCustomFieldPayload(fieldType, value) {
  let normalizedType = fieldType || 'text';

  if (Array.isArray(value)) {
    if (fieldType === 'checkbox') {
      normalizedType = 'checkbox';
    } else if (value.length > 1) {
      normalizedType = 'multi_select';
    } else {
      normalizedType = 'select';
    }
  } else if (fieldType === 'file') {
    normalizedType = 'text';
  }

  return {
    type: normalizedType,
    values: [
      {
        display_order: 0,
        value: Array.isArray(value) ? value.map((item) => ({ content: item })) : { content: value },
      },
    ],
  };
}

function formatDateFields(values) {
  // eslint-disable-next-line complexity
  Object.keys(values).forEach((key) => {
    const value = values[key];
    if (!value || typeof value.format !== 'function') {
      return;
    }

    if (YEAR_FIELDS.includes(key)) {
      values[key] = value.format('YYYY');
      return;
    }

    if (MONTH_FIELDS.includes(key)) {
      values[key] = value.format('MM');
      return;
    }

    values[key] = value.format('YYYY-MM-DD');
  });
}

function buildFieldMap(fields) {
  return fields.reduce((result, field) => {
    result[getStepTwoFieldKey(field)] = field;
    result[field.path] = field;
    return result;
  }, {});
}

function getExistingEntityPayload(profileData, entityPath) {
  const entityRecord = getEntityRecord(profileData, entityPath);
  return entityRecord?.id ? { id: entityRecord.id } : {};
}

function hasPayloadValue(value) {
  if (value === undefined || value === null || value === '') {
    return false;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return true;
}

function hasEntityChanges(entityPayload) {
  return Object.keys(entityPayload).some((key) => key !== 'id');
}

function addCustomFieldValue(target, fieldPath, fieldType, value) {
  target.custom_fields = {
    ...(target.custom_fields || {}),
    [fieldPath]: formatCustomFieldPayload(fieldType, value),
  };
}

function getResourcePayloadValue(resourceFields, fieldPath, value) {
  if (resourceFields.includes(fieldPath)) {
    return { name: value };
  }

  return value;
}

function setEntityFieldValue(target, fieldPath, fieldType, value, defaultFields, resourceFields) {
  if (defaultFields.includes(fieldPath)) {
    target[fieldPath] = getResourcePayloadValue(resourceFields, fieldPath, value);
    return;
  }

  addCustomFieldValue(target, fieldPath, fieldType, value);
}

async function uploadStepTwoFiles(values, fieldMap, profileId) {
  const fileEntries = Object.entries(values.files || {}).filter(([, file]) => file?.originFileObj);

  if (!fileEntries.length) {
    return;
  }

  const uploadedFiles = await Promise.allSettled(
    fileEntries.map(([fieldKey, file]) => {
      const formData = new FormData();
      formData.append('file', file.originFileObj);
      formData.append('csrfmiddlewaretoken', window.csrfToken);

      const fieldDefinition = fieldMap[fieldKey];
      const uploadType = fieldDefinition?.path === 'present_picture' ? 'present_picture' : 'custom_field';

      return axios.post(`/api/v1/profiles/${profileId}/fileupload?type=${uploadType}`, formData).then((response) => ({
        fieldKey,
        uploadedValue: response.data.link,
      }));
    }),
  );

  const failedUploads = [];

  uploadedFiles.forEach((result, index) => {
    const [fieldKey, file] = fileEntries[index];

    if (result.status === 'fulfilled') {
      values[fieldKey] = result.value.uploadedValue;
      return;
    }

    console.error('Failed to upload step two file', {
      fieldKey,
      file: file.originFileObj,
      error: result.reason,
    });
    failedUploads.push({
      fieldKey,
      file: file.originFileObj,
      error: result.reason,
    });
  });

  if (failedUploads.length) {
    const uploadError = new Error('Failed to upload one or more step two files.');
    uploadError.failedUploads = failedUploads;
    throw uploadError;
  }
}

function buildLinkedInPayload(values, profileData) {
  if (!Object.prototype.hasOwnProperty.call(values, 'linkedin_url')) {
    return null;
  }

  const linkedInUrl = values.linkedin_url;
  const existingNonLinkedInLinks = (profileData?.social_links || [])
    .filter((link) => link.type !== LINKEDIN_TYPE && link.type_display !== 'LinkedIn')
    .map((link) => ({
      ...(link.id ? { id: link.id } : {}),
      type: link.type,
      link: link.link,
    }));

  if (!linkedInUrl) {
    return existingNonLinkedInLinks;
  }

  const existingLinkedInLink = (profileData?.social_links || []).find(
    (link) => link.type === LINKEDIN_TYPE || link.type_display === 'LinkedIn',
  );

  return [
    ...existingNonLinkedInLinks,
    {
      ...(existingLinkedInLink?.id ? { id: existingLinkedInLink.id } : {}),
      type: LINKEDIN_TYPE,
      link: linkedInUrl,
    },
  ];
}

// eslint-disable-next-line complexity
export async function buildStepTwoPayload({ formValues, fields, profileData, profileId }) {
  const values = { ...formValues };
  const payload = {};
  const fieldMap = buildFieldMap(fields);
  const educationFields = getExistingEntityPayload(profileData, 'education');
  const otherEducationFields = getExistingEntityPayload(profileData, 'other_education');
  const addressFields = getExistingEntityPayload(profileData, 'address');
  const employmentFields = getExistingEntityPayload(profileData, 'employment');
  const phoneNumberFields = getExistingEntityPayload(profileData, 'phone_number');
  const topLevelCustomFields = {};

  formatDateFields(values);

  await uploadStepTwoFiles(values, fieldMap, profileId);

  const socialLinks = buildLinkedInPayload(values, profileData);
  if (socialLinks) {
    payload.social_links = socialLinks;
  }

  Object.keys(values).forEach((key) => {
    if (['files', 'locationValues', 'linkedin_url'].includes(key)) {
      return;
    }

    const value = values[key];
    if (!hasPayloadValue(value)) {
      return;
    }

    const fieldDefinition = fieldMap[key];
    if (!fieldDefinition || fieldDefinition.type === 'section') {
      return;
    }

    if (key.startsWith('other_education__')) {
      const otherEducationKey = key.replace('other_education__', '');
      setEntityFieldValue(
        otherEducationFields,
        otherEducationKey,
        fieldDefinition.type,
        value,
        ENTITY_DEFAULT_FIELDS.other_education,
        ['field_of_study', 'college', 'degree', 'specialization'],
      );
      return;
    }

    if (key.startsWith('education__')) {
      const educationKey = key.replace('education__', '');
      setEntityFieldValue(educationFields, educationKey, fieldDefinition.type, value, ENTITY_DEFAULT_FIELDS.education, [
        'branch',
        'college',
        'course',
        'specialization',
      ]);
      return;
    }

    if (key.startsWith('employment__')) {
      const employmentKey = key.replace('employment__', '');

      if (ENTITY_DEFAULT_FIELDS.employment.includes(employmentKey)) {
        if (employmentKey === 'current') {
          employmentFields[employmentKey] =
            value === true || value === 'Yes' || (Array.isArray(value) && value.includes('Yes'));
        } else {
          employmentFields[employmentKey] = getResourcePayloadValue(
            ['designation', 'domain', 'employer'],
            employmentKey,
            value,
          );
        }
      } else {
        addCustomFieldValue(employmentFields, employmentKey, fieldDefinition.type, value);
      }
      return;
    }

    if (key.startsWith('address__')) {
      const addressKey = key.replace('address__', '');

      if (values.locationValues && values.locationValues[key]) {
        addressFields[addressKey] = values.locationValues[key];
      } else {
        addressFields[addressKey] = value;
      }
      return;
    }

    if (key.startsWith('phone_number__')) {
      const phoneKey = key.replace('phone_number__', '');
      phoneNumberFields[phoneKey] = value;
      return;
    }

    if (key === 'basic_information__location') {
      payload.location = values.locationValues?.[key] || value;
      return;
    }

    if (fieldDefinition.entity_path === 'basic_information') {
      payload[fieldDefinition.path] = value;
      return;
    }

    topLevelCustomFields[fieldDefinition.path] = formatCustomFieldPayload(fieldDefinition.type, value);
  });

  if (hasEntityChanges(educationFields)) {
    payload.educations = [educationFields];
  }

  if (hasEntityChanges(otherEducationFields)) {
    payload.other_educations = [otherEducationFields];
  }

  if (hasEntityChanges(addressFields)) {
    payload.addresses = [addressFields];
  }

  if (hasEntityChanges(employmentFields)) {
    payload.employments = [employmentFields];
  }

  if (hasEntityChanges(phoneNumberFields)) {
    payload.phone_numbers = [{ ...phoneNumberFields, type: phoneNumberFields.type || 0 }];
  }

  if (Object.keys(topLevelCustomFields).length > 0) {
    payload.custom_fields = topLevelCustomFields;
  }

  return payload;
}
